import { TAXONOMY_DISPLAY, TRADITIONS } from '@archetelos/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer } from '@/features/admin/ui';
import { slugify } from '@/features/composer/types';
import {
  nextSortOrder,
  reorderSwap,
  symbolMutations,
  symbolQueries,
  type ArticleOption,
  type AssetOption,
  type SymbolRow,
} from '@/lib/queries/symbols';

export const Route = createFileRoute('/admin/symbols')({
  component: SymbolsPage,
});

function SymbolsPage() {
  const qc = useQueryClient();
  const list = useQuery(symbolQueries.symbols());
  const rows = [...(list.data ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const [editing, setEditing] = useState<Partial<SymbolRow> | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['symbols'] });
  };

  const reorder = useMutation({
    mutationFn: async ({ row, dir }: { row: SymbolRow; dir: -1 | 1 }) => {
      const swap = reorderSwap(rows, row.id, dir);
      if (!swap) return;
      await symbolMutations.reorderSymbols(swap);
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reorder failed.'),
  });

  const toggleVisible = useMutation({
    mutationFn: async (row: SymbolRow) =>
      symbolMutations.saveSymbol({
        id: row.id,
        name: row.name,
        slug: row.slug,
        blurb: row.blurb,
        tradition_tags: row.tradition_tags,
        article_content_id: row.article_content_id,
        image_asset_id: row.image_asset_id,
        is_visible: !row.is_visible,
        sort_order: row.sort_order,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Update failed.'),
  });

  const remove = useMutation({
    mutationFn: async (row: SymbolRow) => symbolMutations.deleteSymbol(row.id),
    onSuccess: () => {
      toast.success('Symbol deleted.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Reference" title="Symbols" />
      <p className="mt-4 font-sans text-sm text-ink/60">
        Symbols populate the public /symbols page. There is no seed data — an empty list renders the
        page&apos;s empty state. Each symbol&apos;s image resolves to its curated art, then its
        linked article&apos;s cover, then a typographic card.
      </p>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() => setEditing({ is_visible: true, sort_order: nextSortOrder(rows) })}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood"
        >
          Add symbol
        </button>
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">
          No symbols yet. Add one to start building the reference page.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-ink/10">
          {rows.map((r, i) => (
            <li key={r.id} className="flex items-center gap-3 py-3">
              <span className="flex flex-col">
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => reorder.mutate({ row: r, dir: -1 })}
                  className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={i === rows.length - 1}
                  onClick={() => reorder.mutate({ row: r, dir: 1 })}
                  className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </span>
              <button type="button" onClick={() => setEditing(r)} className="flex-1 text-left">
                <span className="font-serif text-base text-ink">{r.name}</span>
                <span className="ml-2 font-sans text-xs text-ink/40">/{r.slug}</span>
                {r.tradition_tags.length > 0 && (
                  <span className="ml-2 font-sans text-xs text-ink/40">
                    · {r.tradition_tags.map((t) => TAXONOMY_DISPLAY[t] ?? t).join(', ')}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => toggleVisible.mutate(r)}
                className={`rounded-sm px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] ${
                  r.is_visible ? 'bg-emerald-600/10 text-emerald-700' : 'bg-ink/10 text-ink/50'
                }`}
              >
                {r.is_visible ? 'Visible' : 'Hidden'}
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(r)}
                className="font-sans text-xs text-oxblood hover:underline disabled:cursor-not-allowed disabled:text-ink/30 disabled:no-underline"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit symbol' : 'New symbol'}
      >
        {editing && (
          <SymbolForm
            symbol={editing}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function SymbolForm({ symbol, onDone }: { symbol: Partial<SymbolRow>; onDone: () => void }) {
  const [name, setName] = useState(symbol.name ?? '');
  const [slug, setSlug] = useState(symbol.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(symbol.slug));
  const [blurb, setBlurb] = useState(symbol.blurb ?? '');
  const [traditions, setTraditions] = useState<string[]>(symbol.tradition_tags ?? []);
  const [articleId, setArticleId] = useState<string | null>(symbol.article_content_id ?? null);
  const [assetId, setAssetId] = useState<string | null>(symbol.image_asset_id ?? null);
  const [isVisible, setIsVisible] = useState(symbol.is_visible ?? true);

  const articles = useQuery(symbolQueries.publishedArticles());
  const assets = useQuery(symbolQueries.assets());

  const toggleTradition = (t: string) =>
    setTraditions((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const save = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const finalSlug = (slugTouched ? slug : slugify(name)).trim();
      const trimmedBlurb = blurb.trim();
      if (!trimmedName) throw new Error('Name is required.');
      if (!finalSlug) throw new Error('Slug is required.');
      if (!trimmedBlurb) throw new Error('Blurb is required.');
      await symbolMutations.saveSymbol({
        id: symbol.id,
        name: trimmedName,
        slug: finalSlug,
        blurb: trimmedBlurb,
        tradition_tags: traditions,
        article_content_id: articleId,
        image_asset_id: assetId,
        is_visible: isVisible,
        sort_order: symbol.sort_order ?? 0,
      });
    },
    onSuccess: () => {
      toast.success('Symbol saved.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  return (
    <div className="space-y-4">
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>

      <Field label="Slug">
        <input
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>

      <Field label="Blurb">
        <textarea
          value={blurb}
          onChange={(e) => setBlurb(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>

      <Field label="Traditions">
        <div className="flex flex-wrap gap-1.5">
          {TRADITIONS.map((t) => {
            const on = traditions.includes(t);
            return (
              <button
                key={t}
                type="button"
                aria-pressed={on}
                onClick={() => toggleTradition(t)}
                className={`rounded-full border px-2.5 py-1 font-sans text-xs transition-colors ${
                  on
                    ? 'border-ink bg-ink text-parchment'
                    : 'border-ink/15 text-ink/60 hover:border-ink/40 hover:text-ink'
                }`}
              >
                {TAXONOMY_DISPLAY[t] ?? t}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Linked article">
        <ArticlePicker
          options={articles.data ?? []}
          value={articleId}
          onChange={setArticleId}
          loading={articles.isLoading}
        />
      </Field>

      <Field label="Symbol art">
        <AssetPicker
          options={assets.data ?? []}
          value={assetId}
          onChange={setAssetId}
          loading={assets.isLoading}
        />
        <p className="mt-1 font-sans text-xs text-ink/50">
          Leave empty to use the linked article&apos;s cover image.
        </p>
      </Field>

      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={isVisible}
          onChange={(e) => setIsVisible(e.target.checked)}
          className="accent-oxblood"
        />
        Visible on the symbols page
      </label>

      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Save symbol
      </button>
    </div>
  );
}

/** Searchable, clearable select over published articles. */
function ArticlePicker({
  options,
  value,
  onChange,
  loading,
}: {
  options: ArticleOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  loading: boolean;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.id === value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 8);
    return options.filter((o) => o.title.toLowerCase().includes(q)).slice(0, 8);
  }, [options, query]);

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-md border border-ink/20 bg-parchment px-3 py-2">
        <span className="font-serif text-sm text-ink">{selected.title}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-ink/40 hover:text-oxblood"
          aria-label="Clear linked article"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={loading ? 'Loading…' : 'Search published articles…'}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      {matches.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-ink/10">
          {matches.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.id);
                  setQuery('');
                }}
                className="block w-full px-3 py-2 text-left font-sans text-sm text-ink/80 hover:bg-ink/5"
              >
                {o.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Searchable, clearable asset picker (curated symbol art). */
function AssetPicker({
  options,
  value,
  onChange,
  loading,
}: {
  options: AssetOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  loading: boolean;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.id === value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 8);
    return options.filter((o) => o.alt_text.toLowerCase().includes(q)).slice(0, 8);
  }, [options, query]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-ink/20 bg-parchment px-3 py-2">
        <span className="flex min-w-0 items-center gap-2">
          <img
            src={selected.public_url}
            alt={selected.alt_text}
            className="h-8 w-8 rounded-sm object-cover"
          />
          <span className="truncate font-sans text-sm text-ink/80">{selected.alt_text}</span>
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-ink/40 hover:text-oxblood"
          aria-label="Clear symbol art"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={loading ? 'Loading…' : 'Search assets by alt text…'}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      {matches.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-ink/10">
          {matches.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.id);
                  setQuery('');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left font-sans text-sm text-ink/80 hover:bg-ink/5"
              >
                <img
                  src={o.public_url}
                  alt={o.alt_text}
                  className="h-8 w-8 rounded-sm object-cover"
                />
                <span className="truncate">{o.alt_text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        {label}
      </label>
      {children}
    </div>
  );
}
